import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { Password } from 'primereact/password';
import {
  addTenant,
  getTenants,
  getSpecificTenant,
  updateTenant,
  getTenantRoles,
} from "../../store/AsyncMethods/TenantMethod";
import { RESET_SPECIFIC_TENANTS } from "../../store/Types/TenantTypes";
import { useTranslation } from "react-i18next";

export default function AddTenant() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const { t } = useTranslation();
  const { user, error } = useSelector((state) => state.AuthReducer);
  const { specificTenant, tenantRoles } = useSelector((state) => state.TenantReducer);

  const isActiveOptions = [
    { name: "Active", value: true },
    { name: "Deactive", value: false },
  ];

  useEffect(() => {
    if (specificTenant && tenantId) {
      formik.setValues({
        fname: specificTenant.first_name || "",
        lname: specificTenant.last_name || "",
        email: specificTenant.email || "",
        role_id: specificTenant.role_id || "",
        isActive: specificTenant.is_active === 1 ? true : false,
      });
    }
  }, [specificTenant, tenantId]);

  useEffect(() => {

    dispatch(getTenantRoles());
  }, []);

  const formik = useFormik({
    initialValues: {
      fname: "",
      lname: "",
      email: "",
      password: "",
      role_id: "",
      isActive: true,
    },
    validationSchema: Yup.object({
      fname: Yup.string().required("First name is required"),
      lname: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string().when([], {
        is: () => !tenantId,
        then: (schema) => schema.min(6, "Password must be at least 6 characters").required("Password is required"),
        otherwise: (schema) => schema.min(6, "Password must be at least 6 characters").notRequired(),
      }),
      role_id: Yup.string().required("Role is required"),
      isActive: Yup.boolean().required("isActive is required"),
    }),

    onSubmit: async (data) => {
      const payload = { ...data };
      if (tenantId && !payload.password) {
        delete payload.password;
      }

      if (tenantId) {
        dispatch(updateTenant(payload, tenantId)).then((success) => {
          if (success) {
            formik.resetForm();
            dispatch({ type: RESET_SPECIFIC_TENANTS });
            dispatch(getTenants());
            navigate("/manage-tenants");
          }
        });
      } else {
        dispatch(addTenant(payload)).then((success) => {
          if (success) {
            formik.resetForm();
            dispatch(getTenants());
            navigate("/manage-tenants");
          }
        });
      }
    },
  });

  

  useEffect(() => {
    if (tenantId) {
      dispatch(getSpecificTenant(tenantId));
    }
  }, [tenantId]);

  return (
    <div className="mx-4">
      <div>
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-black-btn"
          onClick={() => {
            formik.resetForm();
            dispatch({ type: RESET_SPECIFIC_TENANTS });
            navigate(-1);
          }}
        />
      </div>
      <form onSubmit={formik.handleSubmit}>
        <div className="card shadow-md rounded-lg p-4 mt-4">
          <Divider>
            <span className="text-2xl font-bold p-font-style text-center text-primary mx-1">
              {tenantId ? t("edit_tenant") : t("add_tenant")} Tenant
            </span>
          </Divider>

          <div className="px-4 gap-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="fname" className="">
                  {t("first_name")}
                </label>
                <span className=" w-full">
                  <InputText
                    id="fname"
                    name="fname"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.fname}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
              {formik.touched?.fname && formik.errors?.fname && (
                <div className="p-error">{formik.errors?.fname}</div>
              )}
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="lname" className="">
                  {t("last_name")}
                </label>
                <span className=" w-full">
                  <InputText
                    id="lname"
                    name="lname"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.lname}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
              {formik.touched?.lname && formik.errors?.lname && (
                <div className="p-error">{formik.errors?.lname}</div>
              )}
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="">
                  {t("email")}
                </label>
                <span className=" w-full">
                  <InputText
                    id="email"
                    name="email"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
              {formik.touched?.email && formik.errors?.email && (
                <div className="p-error">{formik.errors?.email}</div>
              )}
            </div>
            
            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="password" className="">
                  {tenantId ? t("new_password_optional") : t("password")}
                </label>
                <span className="w-full">
                  <Password
                    id="password"
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    className="w-full p-primary-input"
                    inputClassName="w-full"
                    feedback={!tenantId}
                    toggleMask
                  
                    pt={{
                        input: { className: 'w-full text-lg p-primary-input' },
                        showIcon: {className: "right-2"},
                        hideIcon: {className: "right-2"}
                    }}
                  />
                </span>
              </div>
              {formik.touched?.password && formik.errors?.password && (
                <div className="p-error">{formik.errors?.password}</div>
              )}
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="role_id" className="">
                  {t("role")}
                </label>
                <span className=" w-full">
                  <Dropdown
                    id="role_id"
                    name="role_id"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.role_id}
                    onChange={formik.handleChange}
                    options={tenantRoles}
                    optionLabel="role_name"
                    optionValue="id"
                    pt={{
                        root: { className: "w-full" },
                        input: { className: "w-full p-primary-input" },
                      }}
                  />
                </span>
              </div>
              {formik.touched?.role_id && formik.errors?.role_id && (
                <div className="p-error">{formik.errors?.role_id}</div>
              )}
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="isActive" className="">
                  {t("status")}
                </label>
                <SelectButton
                  name="isActive"
                  id="isActive"
                  options={isActiveOptions}
                  optionLabel="name"
                  optionValue="value"
                  className="flex"
                  pt={{
                    root: { className: "flex" },
                    button: ({ context }) => ({
                      className: context.selected
                        ? "p-primary-highlight-btn w-full text-lg text-center"
                        : "w-full text-lg text-center",
                    }),
                  }}
                  value={formik.values.isActive}
                  onChange={formik.handleChange}
                />
                {formik.touched.isActive && formik.errors.isActive && (
                  <div className="p-error">{formik.errors.isActive}</div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-16">
            <div className="flex justify-end gap-4">
              <Button
                label={t("cancel")}
                icon="pi pi-times"
                className="p-red-btn"
                type="button"
                onClick={() => {
                  formik.resetForm();
                  dispatch({ type: RESET_SPECIFIC_TENANTS });
                  navigate("/manage-tenants");
                }}
              />
              <Button
                label={tenantId ? t("update") : t("submit")}
                icon="pi pi-check"
                className="p-secondary-btn"
                type="submit"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
} 